import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, ToggleLeft, ToggleRight, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATE_COLORS = {
  ok: "bg-emerald-400",
  down: "bg-red-500",
  unknown: "bg-[var(--tt-text-faint)]",
};

export default function AdminSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/sources");
      setSources(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (name, enabled) => {
    setBusy(name);
    try {
      await api.put(`/admin/sources/${name}`, { enabled });
      toast.success(`${name} ${enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(null);
    }
  };

  const setPriority = async (name, priority) => {
    setBusy(name);
    try {
      const clamped = Math.max(-100, Math.min(100, priority));
      await api.put(`/admin/sources/${name}`, { priority: clamped });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Priority update failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-10" data-testid="admin-sources-section">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-xl font-bold">Torrent Sources</h2>
        <span className="text-xs text-[var(--tt-text-muted)] font-mono-tt">
          {sources.filter((s) => s.enabled).length}/{sources.length} active
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-sources-refresh"
          className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-[var(--tt-surface)] hover:bg-[var(--tt-surface-2)] border border-[var(--tt-border)] flex items-center gap-1.5 disabled:opacity-40"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-[var(--tt-text-muted)] p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading sources…
          </div>
        ) : sources.length === 0 ? (
          <div className="text-sm text-[var(--tt-text-muted)] p-6">No sources registered.</div>
        ) : (
          <ul>
            {sources.map((s, i) => (
              <li
                key={s.name}
                data-testid={`admin-source-row-${s.name}`}
                className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? "border-t border-[var(--tt-border)]" : ""}`}
              >
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${STATE_COLORS[s.state]}`}
                  title={`State: ${s.state}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold font-mono-tt">{s.name}</div>
                  <div className="text-[11px] text-[var(--tt-text-muted)] truncate">
                    {s.state === "ok" && (
                      <span>last call: {s.last_results} hits</span>
                    )}
                    {s.state === "down" && s.last_error && (
                      <span className="text-red-400">{s.last_error}</span>
                    )}
                    {s.state === "unknown" && <span>no requests yet</span>}
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                    s.enabled
                      ? "bg-emerald-400/15 text-emerald-400 border border-emerald-400/30"
                      : "bg-red-500/15 text-red-400 border border-red-500/30"
                  }`}
                >
                  {s.enabled ? "Enabled" : "Disabled"}
                </span>

                {/* Priority stepper */}
                <div
                  className="flex items-center gap-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-1.5 py-1"
                  title="Tie-breaker priority (–100 to 100). Higher wins when seeders are equal."
                >
                  <span className="text-[9px] uppercase tracking-wider text-[var(--tt-text-muted)] mr-1">
                    Pri
                  </span>
                  <button
                    type="button"
                    data-testid={`admin-source-prio-down-${s.name}`}
                    onClick={() => setPriority(s.name, (s.priority || 0) - 1)}
                    disabled={busy === s.name || (s.priority || 0) <= -100}
                    className="text-[var(--tt-text-muted)] hover:text-[var(--tt-primary)] disabled:opacity-30"
                    aria-label={`Decrease priority for ${s.name}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <span
                    data-testid={`admin-source-prio-${s.name}`}
                    className={`min-w-[24px] text-center text-xs font-mono-tt font-bold ${
                      (s.priority || 0) > 0
                        ? "text-emerald-400"
                        : (s.priority || 0) < 0
                          ? "text-red-400"
                          : "text-[var(--tt-text)]"
                    }`}
                  >
                    {s.priority || 0}
                  </span>
                  <button
                    type="button"
                    data-testid={`admin-source-prio-up-${s.name}`}
                    onClick={() => setPriority(s.name, (s.priority || 0) + 1)}
                    disabled={busy === s.name || (s.priority || 0) >= 100}
                    className="text-[var(--tt-text-muted)] hover:text-[var(--tt-primary)] disabled:opacity-30"
                    aria-label={`Increase priority for ${s.name}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => toggle(s.name, !s.enabled)}
                  disabled={busy === s.name}
                  data-testid={`admin-source-toggle-${s.name}`}
                  aria-label={s.enabled ? `Disable ${s.name}` : `Enable ${s.name}`}
                  className="flex-shrink-0 text-[var(--tt-text)] hover:text-[var(--tt-primary)] disabled:opacity-50 transition-colors"
                >
                  {busy === s.name ? (
                    <Loader2 className="w-7 h-7 animate-spin" />
                  ) : s.enabled ? (
                    <ToggleRight className="w-9 h-9 text-emerald-400" />
                  ) : (
                    <ToggleLeft className="w-9 h-9 text-[var(--tt-text-muted)]" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
